import LoadingButton from '@mui/lab/LoadingButton'
import { Box, Button, Grid, styled, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { FC, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useExportEvents } from '@/api/campaigns/campaign-export-events'
import { useGetCampaignReport } from '@/api/campaigns/campaign-report'
import { useCampaign } from '@/api/campaigns/get'
import { NEW_LAUNCH_ANIMATION_DELAY_TIME } from '@/api/constants'
import TooFast from '@/assets/images/too-fast.png'
import FeedbackAnimation from '@/common/animations/FeedbackAnimation'
import LayoutPageContent from '@/common/components/Layout/LayoutPageContent'
import PageHeader from '@/common/components/Layout/LayoutPageContent/PageHeader'
import Popup from '@/common/components/Popup'
import { AssetType } from '@/types/campaigns'
import { useTranslation } from 'react-i18next'
import CampaignMembers from './components/CampaignMembers'
import CampaignMembersInteractions from './components/CampaignMembersInteractions'
import CampaignPreview from './components/CampaignPreview'
import CampaignScore from './components/CampaignScore'
import CampaignSummary from './components/CampaignSummary'
import CampaignTimeFrame from './components/CampaignTimeFrame'
import CampaignVideoPackagePreview from './components/CampaignVideoPackagePreview'
import CampaignVideoPreview from './components/CampaignVideoPreview'
import CampaignVideoQuizResults from './components/CampaignVideoQuizResults'

const CampaignDashboardNew: FC = () => {
  const { _id } = useParams()
  const { data: campaignData } = useCampaign(_id)

  const type = campaignData?.asset_type
  const includeQuiz: boolean = !!campaignData?.asset_metadata?.include_quiz

  if (!_id) return null // handle missing campaings id

  const isNEW =
    campaignData?.created_at &&
    dayjs().valueOf() -
      dayjs(campaignData?.created_at).add(dayjs(campaignData?.created_at).utcOffset(), 'minutes').valueOf() <
      NEW_LAUNCH_ANIMATION_DELAY_TIME

  return (
    <LayoutPageContent>
      <CampaignDashboardNewHeader campaignId={_id} />

      <Grid item xs={12}>
        <CampaignSummary campaignId={_id} />
      </Grid>

      <Grid item xs={12}>
        <CampaignMembersInteractions campaignId={_id} />
      </Grid>

      <Grid item xs={6} minWidth={480}>
        <CampaignScore campaignId={_id} />
      </Grid>

      <Grid item xs={6}>
        <CampaignTimeFrame campaignId={_id} />
      </Grid>

      {type === AssetType.phishing_simulation && <CampaignPreview campaignId={_id} />}

      {/* {type === AssetType.training_video && includeQuiz && (
        <>
          <Grid item xs={8}>
            <MembersActivityOverTime campaignId={_id} />
          </Grid>
          <Grid item xs={4}>
            <CampaignQuizAverageScore campaignId={_id} />
          </Grid>
        </>
      )} */}
      {/* 
      This template should cover also the video training campaign dashboard

      Video Training components
      Average Quiz Score
      Recipients Quiz Results
      Members Activity Over Time - done V

      */}
      <Grid item xs={12}>
        <CampaignMembers campaignId={_id} />
      </Grid>

      {type === AssetType.training_video &&
        (campaignData?.assets?.length && campaignData?.assets?.length > 1 ? (
          <Grid item xs={12}>
            <CampaignVideoPackagePreview campaignId={_id} />
          </Grid>
        ) : (
          <>
            <Grid item xs={12}>
              <CampaignVideoPreview campaignId={_id} />
            </Grid>
            {includeQuiz && (
              <Grid item xs={12} minWidth={900}>
                <CampaignVideoQuizResults campaignId={_id} />
              </Grid>
            )}
          </>
        ))}
      {isNEW && <FeedbackAnimation />}
    </LayoutPageContent>
  )
}

const CampaignDashboardNewHeader: FC<{ campaignId: string }> = ({ campaignId }) => {
  const { t } = useTranslation()
  const [isReportAlertOpen, setReportAlertOpen] = useState(false)

  const { data: campaignData } = useCampaign(campaignId)

  const { mutateAsync: getCampaignReport, isPending: isReportLoading } = useGetCampaignReport(() =>
    setReportAlertOpen(true)
  )

  const { mutateAsync: exportEvents } = useExportEvents()

  return (
    <PageHeader>
      {isReportAlertOpen && (
        <Popup
          open={isReportAlertOpen}
          onClose={() => setReportAlertOpen(false)}
          onConfirm={() => setReportAlertOpen(false)}
          buttonLabels={{
            confirm: t('trainingCampaignDashboard.reportAlert.acknowledge'),
          }}
          icon={TooFast}>
          <h4>{t('trainingCampaignDashboard.reportAlert.title')}</h4>
          <Typography>{t('trainingCampaignDashboard.reportAlert.message')}</Typography>
        </Popup>
      )}
      <Box display="flex" gap={1}>
        {/* <Button onClick={() => {}} size="small" disabled>
            <Tooltip title={t('campaignDashboard.campaignSummary.buttonTooltipEditCampaigs')}>
              <FontAwesomeIcon icon={faPenToSquare} />
            </Tooltip>
          </Button> */}
        <LoadingButtonStyled
          variant="outlined"
          size="medium"
          onClick={() => getCampaignReport(campaignId)}
          disabled={campaignData?.status !== 'completed' || isReportLoading} /*|| !users?.length */
          loading={isReportLoading}>
          {t('campaignDashboard.downloadReport')}
        </LoadingButtonStyled>
        <ButtonStyled
          variant="contained"
          size="medium"
          disabled={!campaignData?.events.email_open}
          onClick={() => exportEvents(campaignId)}>
          {t('campaignDashboard.downloadEvents')}
        </ButtonStyled>
      </Box>
    </PageHeader>
  )
}

const LoadingButtonStyled = styled(LoadingButton)(({ theme }) => ({
  '&.Mui-disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
  },
}))

const ButtonStyled = styled(Button)(({ theme }) => ({
  '&.Mui-disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
  },
}))

export default CampaignDashboardNew
